import { ChipsDropdownOption } from './CategorySelectorDropdown';
import { Category, CategoryEntry } from './types';

/**
 * MEMO: categoriesByLevels 제거시 전체 삭제 예정
 * - isDirectlySelected, hasSelectedParent, getEnabledChildrenKeys, unmergeSiblingsKeys, mergeSiblingsKeys, removeChildrenKeys, removeParentKeys
 */
// 카테고리가 직접 선택되었는지 확인
export const isDirectlySelected = (category: Category, selectedKeys: Array<string>): boolean => {
  return selectedKeys.includes(category.key);
};

// 카테고리의 부모가 선택되어 자식이 체크된 상태인지 확인
export const hasSelectedParent = (
  category: Category,
  categoryMap: Map<string, Category>,
  selectedKeys: Array<string>,
): boolean => {
  if (!category.parentKey) return false;
  const parent = categoryMap.get(category.parentKey);
  if (!parent) return false;
  return isDirectlySelected(parent, selectedKeys) || hasSelectedParent(parent, categoryMap, selectedKeys);
};

// 활성화된 자식 키들을 반환하는 헬퍼 함수
const getEnabledChildrenKeys = (category: Category, categoryMap: Map<string, Category>): Array<string> => {
  return (
    category.childrenKeys?.filter((key) => {
      const childCategory = categoryMap.get(key);
      return childCategory && !childCategory.disabled;
    }) || []
  );
};

/**
 * 부모 선택을 자식 선택으로 "분해"하는 함수
 * @param selected_keys 현재 선택된 키들
 * @param category 대상 카테고리
 * @returns 부모 선택이 자식 선택으로 분리된 키들
 */
export const unmergeSiblingsKeys = (
  selected_keys: string[],
  category: Category,
  categoryMap: Map<string, Category>,
): string[] => {
  const parentCategory = categoryMap.get(category.parentKey);
  if (!parentCategory) {
    return selected_keys;
  }
  const unmerged_selected_keys = unmergeSiblingsKeys(selected_keys, parentCategory, categoryMap);
  if (unmerged_selected_keys.includes(parentCategory.key)) {
    const enabledChildrenKeys = getEnabledChildrenKeys(parentCategory, categoryMap);
    return [
      ...unmerged_selected_keys.filter((selectedKey) => selectedKey !== parentCategory.key),
      ...enabledChildrenKeys,
    ];
  }
  return unmerged_selected_keys;
};

/**
 * 모든 자식이 선택되었을 때 부모로 병합하는 함수(형제 카테고리들이 모두 선택되었을 때만 부모로 병합)
 * @param selected_keys 현재 선택된 키들
 * @param category 대상 카테고리
 * @returns 자식 선택이 부모 선택으로 병합된 키들
 */
export const mergeSiblingsKeys = (
  selected_keys: string[],
  category: Category,
  categoryMap: Map<string, Category>,
): string[] => {
  const parentCategory = categoryMap.get(category.parentKey);
  if (!parentCategory) {
    return selected_keys;
  }
  const enabledChildrenKeys = getEnabledChildrenKeys(parentCategory, categoryMap);

  if (enabledChildrenKeys.every((key) => selected_keys.includes(key))) {
    return mergeSiblingsKeys(
      [...selected_keys.filter((selectedKey) => !enabledChildrenKeys.includes(selectedKey)), parentCategory.key],
      parentCategory,
      categoryMap,
    );
  }
  return selected_keys;
};

/**
 * 특정 카테고리의 모든 자식 선택을 해제하는 함수
 * @param selected_keys 현재 선택된 키들
 * @param category 대상 카테고리
 * @returns 자식 선택이 해제된 키들
 */
export const removeChildrenKeys = (
  selected_keys: string[],
  category: Category,
  categoryMap: Map<string, Category>,
): string[] => {
  const enabledChildrenKeys = getEnabledChildrenKeys(category, categoryMap);
  const filtered_selected_keys = selected_keys.filter((selectedKey) => !enabledChildrenKeys.includes(selectedKey));

  if (!category.childrenKeys || category.childrenKeys.length === 0) {
    return filtered_selected_keys;
  }

  return category.childrenKeys.reduce((acc_selected_keys, childKey) => {
    const childCategory = categoryMap.get(childKey);
    if (!childCategory) {
      return acc_selected_keys;
    }
    return removeChildrenKeys(acc_selected_keys, childCategory, categoryMap);
  }, filtered_selected_keys);
};

/**
 * 특정 카테고리의 모든 부모 선택을 해제하는 함수
 * @param selected_keys 현재 선택된 키들
 * @param category 대상 카테고리
 * @returns 부모 선택이 해제된 키들
 */
export const removeParentKeys = (
  selected_keys: string[],
  category: Category,
  categoryMap: Map<string, Category>,
): string[] => {
  const filtered_selected_keys = selected_keys.filter((selectedKey) => selectedKey !== category.parentKey);

  const parentCategory = categoryMap.get(category.parentKey);
  if (!parentCategory) {
    return filtered_selected_keys;
  }

  return removeParentKeys(filtered_selected_keys, parentCategory, categoryMap);
};

export const getCategoryMaxDepth = (items: CategoryEntry[]): number => {
  if (!items || items.length === 0) return 1;

  let maxDepth = 1;
  const stack: Array<{ items: CategoryEntry[]; depth: number }> = [{ items, depth: 1 }];

  while (stack.length > 0) {
    const { items: currentItems, depth } = stack.pop()!;
    maxDepth = Math.max(maxDepth, depth);

    for (const item of currentItems) {
      if (item.children && item.children.length > 0) {
        stack.push({ items: item.children, depth: depth + 1 });
      }
    }
  }

  return maxDepth;
};

const findCategoryPath = (items: CategoryEntry[], targetId: string, currentPath: string[] = []): string[] | null => {
  for (const item of items) {
    const newPath = [...currentPath, item.name];

    if (item.id === targetId) {
      return newPath;
    }

    if (item.children) {
      const result = findCategoryPath(item.children, targetId, newPath);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const generateOptionsFromKeys = (
  items: CategoryEntry[],
  selectedKeys: Array<string>,
): Array<ChipsDropdownOption<string>> => {
  return selectedKeys.reduce<Array<ChipsDropdownOption<string>>>((acc, key) => {
    if (!key || key.trim() === '') return acc;
    const path = findCategoryPath(items, key);
    const label = path ? path.join('/') : key;
    if (label && label.trim() !== '') {
      acc.push({ label, value: key });
    }
    return acc;
  }, []);
};
